import { useEffect } from "react";
import { useRouter } from "next/router";
import { GetStaticPropsContext, InferGetStaticPropsType } from "next";

import { api, RecipeAndStepsQuery, storeWrapper } from "@chef/state-management";
import { findFeaturedPortraitImage } from "@chef/state-management/helpers";
import { AppShell } from "@chef/feature-app-shell";
import { BRAND_PATHS } from "@chef/constants";
import { slugify } from "@chef/helpers";
import { isEqualStrings } from "@chef/utils/equal";

import { Recipe } from "../libs/recipe/Recipe";

import { intl } from "./RecipePage.Intl";

export const RecipePage = ({
  recipe,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
  const router = useRouter();
  const { print, portions } = router.query;

  const { recipeName, recipeDescription, recipeNameHeadline } = recipe;
  const metaImage = findFeaturedPortraitImage(recipe)?.url;

  useEffect(() => {
    if (!print) {
      return;
    }

    window.print();
  }, [print]);

  const canonical = BRAND_PATHS.RECIPE_WITH_NAME_HREF({
    recipeId: recipe.mainRecipeId || recipe.recipeId,
    recipeName: slugify(recipeName),
  });

  const name = recipeNameHeadline || recipeName;

  const portionSizes = recipe?.instructions?.portions
    .map((portion) => portion.size.toString())
    .sort();

  const portionsParamIsValid = portionSizes.some((p) =>
    isEqualStrings(p, portions as string),
  );

  const overridePortionSize = portionsParamIsValid
    ? (portions as string)
    : undefined;

  return (
    <AppShell
      title={`${name} - ${intl.RECIPE}`}
      description={recipeDescription}
      image={metaImage}
      canonical={canonical}
    >
      <AppShell.Meta />
      <AppShell.Drawer />
      <AppShell.Header printHidden />

      <AppShell.Main transparent>
        <Recipe
          recipe={recipe}
          view="static-conversion"
          overridePortionSize={overridePortionSize}
        />
      </AppShell.Main>

      <AppShell.Footer />
    </AppShell>
  );
};

export const getStaticProps = storeWrapper.getStaticProps(
  (store) =>
    async ({
      params,
    }: GetStaticPropsContext): Promise<
      | {
          props: { recipe: RecipeAndStepsQuery["recipeAndSteps"] };
          revalidate: number;
        }
      | {
          redirect: { destination: string; permanent: boolean };
          revalidate: number;
        }
    > => {
      const id = params?.["id"];

      // Check that the id is a valid number
      if (!id || isNaN(+id)) {
        return {
          redirect: {
            destination: "/",
            permanent: false,
          },
          revalidate: 600,
        };
      }

      try {
        const { recipeAndSteps } = await store
          .dispatch(
            api.endpoints.recipeAndSteps.initiate({
              recipeId: +id,
            }),
          )
          .unwrap();

        return {
          props: {
            recipe: recipeAndSteps,
          },
          revalidate: 600,
        };
      } catch (e) {
        return {
          redirect: {
            destination: BRAND_PATHS.RECIPE_HUB_PATH,
            permanent: false,
          },
          revalidate: 600,
        };
      }
    },
);

export const getStaticPaths = () => {
  return {
    paths: [],
    fallback: "blocking",
  };
};
